import { ImageState, UserImageLibraryCollection, VSCAssetImageSourceName } from 'js/types';
import cloneDeep from 'lodash.clonedeep';
import { PLAY_FROM_EDITOR, PlayFromElementEditorAction } from 'js/actionCreators/playbackActions';

import {
  GET_IMAGE_LIBRARIES_SUCCESS,
  GET_USER_IMAGES_SUCCESS,
  DELETE_USER_IMAGE_SUCCESS,
  GetImageLibrariesSuccessAction,
  GetUserImagesFailureAction,
  GetUserImagesSuccessAction,
  DeleteUserImageSuccessAction,
  GET_IMAGE_LIBRARIES,
  GetImageLibrariesFailureAction,
  GET_IMAGE_LIBRARIES_FAILURE,
  GetImageLibrariesAction,
  PrependUserImagesListAction,
  PREPEND_USER_IMAGES_LIST,
  LoadPageOfUserImagesSuccessAction,
  LOAD_PAGE_OF_USER_IMAGES_SUCCESS,
  LOAD_PAGE_OF_USER_IMAGES,
  LoadPageOfUserImagesAction,
  ADD_USER_IMAGE_TO_SCRIBE,
  AddUserImageToScribeAction,
  START_REPLACE_IMAGE,
  StartReplaceImageAction,
  CLEAR_REPLACE_IMAGE,
  ClearReplaceImageAction
} from '../actionCreators/imagesActions';

import { filterSourceAssetsIntoImageLibraries } from './reducerHelpers/filterSourceAssetsIntoImageLibraries';

const initializeUserLibrary = () => {
  const userLibrary = {} as UserImageLibraryCollection;

  Object.values(VSCAssetImageSourceName).forEach(sourceName => {
    userLibrary[sourceName] = {
      loading: false,
      loadingSortedByLastUsed: false,
      sourceAssets: [],
      images: [],
      imagesSortedByLastUsed: []
    };
  });

  return userLibrary;
};

const initialState: ImageState = {
  loadingLibraries: false,
  libraries: [],
  userLibrary: initializeUserLibrary(),
  replacingImageElementId: null
};

type ImagesAction =
  | GetImageLibrariesSuccessAction
  | GetUserImagesFailureAction
  | GetUserImagesSuccessAction
  | DeleteUserImageSuccessAction
  | GetImageLibrariesAction
  | GetImageLibrariesFailureAction
  | PrependUserImagesListAction
  | LoadPageOfUserImagesAction
  | LoadPageOfUserImagesSuccessAction
  | AddUserImageToScribeAction
  | StartReplaceImageAction
  | ClearReplaceImageAction
  | PlayFromElementEditorAction;

export default function imagesReducer(state = initialState, action: ImagesAction): ImageState {
  switch (action.type) {
    case GET_IMAGE_LIBRARIES: {
      return {
        ...state,
        loadingLibraries: true
      };
    }

    case GET_IMAGE_LIBRARIES_SUCCESS:
      return {
        ...state,
        loadingLibraries: false,
        libraries: action.libraries
      };

    case GET_IMAGE_LIBRARIES_FAILURE: {
      return {
        ...state,
        loadingLibraries: false
      };
    }

    case GET_USER_IMAGES_SUCCESS: {
      const userLibrary = filterSourceAssetsIntoImageLibraries(state.userLibrary, action.payload);

      return {
        ...state,
        userLibrary
      };
    }

    case LOAD_PAGE_OF_USER_IMAGES: {
      const loadingKey = action.sortedByLastUsed ? 'loadingSortedByLastUsed' : 'loading';

      return {
        ...state,
        userLibrary: {
          ...state.userLibrary,
          [action.imageType]: {
            ...state.userLibrary[action.imageType],
            [loadingKey]: true
          }
        }
      };
    }

    case LOAD_PAGE_OF_USER_IMAGES_SUCCESS: {
      const loadingKey = action.sortedByLastUsed ? 'loadingSortedByLastUsed' : 'loading';
      const imageListKey = action.sortedByLastUsed ? 'imagesSortedByLastUsed' : 'images';
      const imageList = state.userLibrary[action.imageType][imageListKey];
      const updatedImageList = imageList.concat(action.images);

      return {
        ...state,
        userLibrary: {
          ...state.userLibrary,
          [action.imageType]: {
            ...state.userLibrary[action.imageType],
            [imageListKey]: updatedImageList,
            [loadingKey]: false
          }
        }
      };
    }

    case DELETE_USER_IMAGE_SUCCESS: {
      const targetLibrary = cloneDeep(state.userLibrary[action.targetLibrary]);
      targetLibrary.images = targetLibrary.images.filter(img => img.id !== action.imageId);
      targetLibrary.imagesSortedByLastUsed = targetLibrary.imagesSortedByLastUsed.filter(
        img => img.id !== action.imageId
      );
      targetLibrary.sourceAssets = targetLibrary.sourceAssets.filter(img => img.id !== action.imageId);

      return {
        ...state,
        userLibrary: {
          ...state.userLibrary,
          [action.targetLibrary]: targetLibrary
        }
      };
    }

    case PREPEND_USER_IMAGES_LIST: {
      const userUploadLibrary = cloneDeep(state.userLibrary[action.targetLibrary]);

      userUploadLibrary.images = [...action.images, ...userUploadLibrary.images];
      userUploadLibrary.imagesSortedByLastUsed = [...action.images, ...userUploadLibrary.imagesSortedByLastUsed];
      userUploadLibrary.sourceAssets = [...action.images, ...userUploadLibrary.sourceAssets];

      return {
        ...state,
        userLibrary: {
          ...state.userLibrary,
          [action.targetLibrary]: userUploadLibrary
        }
      };
    }

    case ADD_USER_IMAGE_TO_SCRIBE: {
      const { imageLibrarySource, actionTime, imageId } = action;

      if (imageLibrarySource) {
        const updateTime = actionTime.toISOString();
        const library = cloneDeep(state.userLibrary[imageLibrarySource]);
        const sourceAsset = library.sourceAssets.find(asset => asset.projectAssetId === imageId);
        const imageLibAsset = library.images.find(asset => asset.projectAssetId === imageId);
        const imagesSortedByLastUsedAsset = library.imagesSortedByLastUsed.find(
          asset => asset.projectAssetId === imageId
        );

        if (sourceAsset) {
          sourceAsset.lastUsed = updateTime;
        }

        if (imageLibAsset) {
          imageLibAsset.lastUsed = updateTime;
        }

        if (imagesSortedByLastUsedAsset) {
          imagesSortedByLastUsedAsset.lastUsed = updateTime;
        }

        const resortedImages = library.imagesSortedByLastUsed.toSorted((a, b) => {
          const dateA = new Date(a.lastUsed || a.createdDate);
          const dateB = new Date(b.lastUsed || b.createdDate);
          return dateB.getTime() - dateA.getTime();
        });

        library.imagesSortedByLastUsed = resortedImages;

        return {
          ...state,
          userLibrary: {
            ...state.userLibrary,
            [imageLibrarySource]: library
          }
        };
      }

      return state;
    }

    case START_REPLACE_IMAGE: {
      return {
        ...state,
        replacingImageElementId: action.elementId
      };
    }

    case PLAY_FROM_EDITOR:
    case CLEAR_REPLACE_IMAGE: {
      return {
        ...state,
        replacingImageElementId: null
      };
    }

    default:
      return state;
  }
}
